<template>
	<el-dialog v-dialogDrag :title="dialogTitle" :visible.sync="show" width="820px" :close-on-click-modal="false"
		@close="close">
		<div class="favorite-selector" v-loading="loading">
			<div class="left-box">
				<div class="search-wrap">
					<el-input :placeholder="$t('favorite.search')" v-model="searchText" size="small" clearable
						prefix-icon="el-icon-search">
					</el-input>
				</div>
				<div class="nav-menu">
					<div v-for="item in menuItems" :key="item.key" class="nav-item"
						:class="{ active: currentCategory === item.key }" @click="currentCategory = item.key">
						<i :class="item.icon"></i>
						<span>{{ item.label }}</span>
					</div>
				</div>
			</div>
			<div class="right-box">
				<div class="content-header">
					<span class="header-title">{{ headerTitle }}</span>
				</div>
				<div class="content-body">
					<no-data-tip v-if="!loading && filteredFavorites.length === 0" class="no-data-tip"></no-data-tip>
					<div v-else class="favorite-list">
						<favorite-message-item v-for="item in filteredFavorites" :key="item.id" :favorite="item"
							:select-mode="true" :pick-mode="true" :selected="isSelected(item)" @click="toggleSelect"
							@select-change="onSelectChange">
						</favorite-message-item>
					</div>
				</div>
			</div>
		</div>
		<span slot="footer" class="dialog-footer">
			<el-button @click="close()">{{ $t('common.cancel') }}</el-button>
			<el-button type="primary" :disabled="selectedItems.length === 0" @click="ok()">{{ $t('common.send')
			}}</el-button>
		</span>
	</el-dialog>
</template>

<script>
import FavoriteMessageItem from './FavoriteMessageItem.vue';
import NoDataTip from '../common/NoDataTip.vue';

export default {
	name: 'FavoriteSelector',
	components: {
		FavoriteMessageItem,
		NoDataTip
	},
	props: {
		targetName: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			show: false,
			loading: false,
			searchText: '',
			currentCategory: 'all',
			favorites: [],
			selectedItems: [],
			callback: null,
			maxSelect: 20
		};
	},
	computed: {
		dialogTitle() {
			return this.$t('favorite.sendTitle', { name: this.targetName || '' });
		},
		menuItems() {
			return [
				{ key: 'all', label: this.$t('favorite.all'), icon: 'icon iconfont icon-fav-all' },
				{ key: 'text', type: this.$enums.MESSAGE_TYPE.TEXT, label: this.$t('favorite.text'), icon: 'el-icon-document' },
				{ key: 'image', type: this.$enums.MESSAGE_TYPE.IMAGE, label: this.$t('favorite.image'), icon: 'el-icon-picture-outline' },
				{ key: 'sticker', type: this.$enums.MESSAGE_TYPE.STICKER, label: this.$t('favorite.sticker'), icon: 'icon iconfont icon-emoji' }
			].filter(item => item.key === 'all' || this.$msgType.isSupported(item.type));
		},
		currentCategoryItem() {
			return this.menuItems.find(item => item.key === this.currentCategory) || this.menuItems[0];
		},
		headerTitle() {
			if (this.selectedItems.length > 0) {
				return this.$t('favorite.selectedCount', { n: this.selectedItems.length });
			}
			return this.currentCategoryItem.label;
		},
		filteredFavorites() {
			let list = this.favorites.filter(item => this.$msgType.isSupported(item.messageType));
			if (this.currentCategory !== 'all') {
				const typeMap = {
					text: this.$enums.MESSAGE_TYPE.TEXT,
					image: this.$enums.MESSAGE_TYPE.IMAGE,
					sticker: this.$enums.MESSAGE_TYPE.STICKER
				};
				const messageType = typeMap[this.currentCategory];
				list = list.filter(item => item.messageType === messageType);
			}
			const keyword = this.searchText.trim().toLowerCase();
			if (keyword) {
				list = list.filter(item => {
					if (item.chatName.toLowerCase().includes(keyword)) {
						return true;
					}
					if (item.messageType === this.$enums.MESSAGE_TYPE.TEXT) {
						return (item.content).toLowerCase().includes(keyword);
					}
					const contentData = JSON.parse(item.content);
					if (contentData.name) {
						console.log("contentData.name:",contentData.name)
						return contentData.name.toLowerCase().includes(keyword);
					}
					return false;
				});
			}
			return list;
		}
	},
	methods: {
		open(callback) {
			this.callback = callback;
			this.searchText = '';
			this.currentCategory = 'all';
			this.selectedItems = [];
			this.show = true;
			this.loadFavorites();
		},
		close() {
			this.show = false;
			this.callback = null;
			this.selectedItems = [];
		},
		loadFavorites() {
			this.loading = true;
			this.$http({
				url: '/favorite/list',
				method: 'get'
			}).then(list => {
				this.favorites = list || [];
			}).finally(() => {
				this.loading = false;
			});
		},
		isSelected(item) {
			return this.selectedItems.some(f => f.id === item.id);
		},
		toggleSelect(item) {
			if (this.isSelected(item)) {
				this.selectedItems = this.selectedItems.filter(f => f.id !== item.id);
				return;
			}
			if (this.selectedItems.length >= this.maxSelect) {
				this.$message.warning(this.$t('chat.maxSelect20'));
				return;
			}
			this.selectedItems.push(item);
		},
		onSelectChange(item, checked) {
			if (checked) {
				if (!this.isSelected(item)) {
					if (this.selectedItems.length >= this.maxSelect) {
						this.$message.warning(this.$t('chat.maxSelect20'));
						return;
					}
					this.selectedItems.push(item);
				}
			} else {
				this.selectedItems = this.selectedItems.filter(f => f.id !== item.id);
			}
		},
		async ok() {
			if (this.selectedItems.length === 0) {
				this.$message.warning(this.$t('favorite.selectToSend'));
				return;
			}
			const items = [...this.selectedItems];
			this.callback && this.callback(items);
			this.close();
		}
	}
}
</script>

<style lang="scss" scoped>
.favorite-selector {
	display: flex;
	height: 520px;
	background: var(--im-background);

	.left-box {
		width: 210px;
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		background: var(--im-background);
		border-right: 1px solid rgba(0, 0, 0, 0.08);
		box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);

		.search-wrap {
			padding: 12px 12px 8px;
			border-bottom: var(--im-border);
		}

		.nav-menu {
			flex: 1;
			overflow-y: auto;
			padding: 8px 0;

			.nav-item {
				display: flex;
				align-items: center;
				gap: 10px;
				height: 44px;
				margin: 0 6px 2px;
				padding: 0 12px;
				border-radius: 10px;
				cursor: pointer;
				color: var(--im-text-color);
				white-space: nowrap;
				transition: background-color 0.15s ease;

				&:hover {
					background-color: var(--im-background-active);
				}

				&.active {
					background-color: var(--im-background-active-dark);
					color: var(--im-color-primary);

					i {
						color: var(--im-color-primary);
					}
				}

				i {
					font-size: 18px;
					width: 20px;
					text-align: center;
				}

			}
		}
	}

	.right-box {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		background: #fcfdff;

		.content-header {
			height: 52px;
			flex-shrink: 0;
			display: flex;
			align-items: center;
			padding: 0 20px;
			border-bottom: var(--im-border);
			background: var(--im-background);

			.header-title {
				font-size: var(--im-font-size-larger);
				font-weight: 600;
				color: #333;
			}
		}

		.content-body {
			flex: 1;
			min-height: 0;
			overflow-y: auto;
			padding: 16px 20px;
			background: #fafbfc;
			display: flex;
			flex-direction: column;

			.no-data-tip {
				flex: 1;
			}

			.favorite-list {
				display: flex;
				flex-direction: column;
				gap: 12px;
			}
		}
	}
}
</style>
